// This file is auto-generated by @hey-api/openapi-ts

import { client } from './Client.gen';
import type { EshopApplePayControllerGetApplePayEchoData } from './EshopApplePayControllerGetApplePayEchoData.gen';
import type { EshopApplePayControllerGetApplePayEchoErrors } from './EshopApplePayControllerGetApplePayEchoErrors.gen';
import type { EshopApplePayControllerGetApplePayEchoResponses } from './EshopApplePayControllerGetApplePayEchoResponses.gen';
import type { EshopApplePayControllerInitApplePayPaymentData } from './EshopApplePayControllerInitApplePayPaymentData.gen';
import type { EshopApplePayControllerInitApplePayPaymentErrors } from './EshopApplePayControllerInitApplePayPaymentErrors.gen';
import type { EshopApplePayControllerInitApplePayPaymentResponses } from './EshopApplePayControllerInitApplePayPaymentResponses.gen';
import type { EshopApplePayControllerProcessApplePayPaymentData } from './EshopApplePayControllerProcessApplePayPaymentData.gen';
import type { EshopApplePayControllerProcessApplePayPaymentErrors } from './EshopApplePayControllerProcessApplePayPaymentErrors.gen';
import type { EshopApplePayControllerProcessApplePayPaymentResponses } from './EshopApplePayControllerProcessApplePayPaymentResponses.gen';
import type { EshopApplePayControllerValidateMerchantData } from './EshopApplePayControllerValidateMerchantData.gen';
import type { EshopApplePayControllerValidateMerchantErrors } from './EshopApplePayControllerValidateMerchantErrors.gen';
import type { EshopApplePayControllerValidateMerchantResponses } from './EshopApplePayControllerValidateMerchantResponses.gen';
import type { Options } from './Options.gen';

export class EshopApplePayController {
    /**
     * Returns Apple Pay configuration parameters required for client-side initialization.
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **APPLE_PAY_ECHO_ERROR** - Error from payment gateway echo.
     */
    public static getApplePayEcho<ThrowOnError extends boolean = false>(options?: Options<EshopApplePayControllerGetApplePayEchoData, ThrowOnError>) {
        return (options?.client ?? client).get<EshopApplePayControllerGetApplePayEchoResponses, EshopApplePayControllerGetApplePayEchoErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/payments/apple-pay/echo',
            ...options
        });
    }
    
    /**
     * Performs Apple Pay merchant validation against Apple servers and returns the merchant session.
     *
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **INVALID_APPLE_PAY_VALIDATION_URL** - Validation URL is not a secure Apple Pay endpoint.<br>
     * + **APPLE_PAY_MERCHANT_VALIDATION_ERROR** - Apple Pay merchant validation failed.
     */
    public static validateMerchant<ThrowOnError extends boolean = false>(options: Options<EshopApplePayControllerValidateMerchantData, ThrowOnError>) {
        return (options.client ?? client).post<EshopApplePayControllerValidateMerchantResponses, EshopApplePayControllerValidateMerchantErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/payments/apple-pay/validate-merchant',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Initializes a new Apple Pay payment at the payment gateway.
     *
     * Returns the gateway response. When it contains `actions.fingerprint.browserInit`, the client must perform the device fingerprint (load the URL in a hidden iframe) and then call `/process`.<br>
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **ORDER_USER_MISMATCH** - The order and logged user doesn't match.
     */
    public static initApplePayPayment<ThrowOnError extends boolean = false>(options: Options<EshopApplePayControllerInitApplePayPaymentData, ThrowOnError>) {
        return (options.client ?? client).post<EshopApplePayControllerInitApplePayPaymentResponses, EshopApplePayControllerInitApplePayPaymentErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/payments/apple-pay/init',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
    
    /**
     * Processes an initialized Apple Pay payment (after the optional device fingerprint).
     *
     * Returns the gateway response. When it contains `actions.authenticate.browserChallenge`, the client must open the URL (3DS challenge); the customer is then redirected back to the eshop return URL.<br>
     * ### Error names for HTTP Status 400 BadRequest:<br>
     * + **VALIDATION_ERROR** - Invalid body, check 'errors' property for more info.<br>
     * + **ORDER_USER_MISMATCH** - The order and logged user doesn't match.<br>
     * + **CANNOT_INIT_PAYMENT** - CSOB Gateway cannot be initialized.
     */
    public static processApplePayPayment<ThrowOnError extends boolean = false>(options: Options<EshopApplePayControllerProcessApplePayPaymentData, ThrowOnError>) {
        return (options.client ?? client).post<EshopApplePayControllerProcessApplePayPaymentResponses, EshopApplePayControllerProcessApplePayPaymentErrors, ThrowOnError>({
            security: [{ scheme: 'bearer', type: 'http' }],
            url: '/api/eshop/payments/apple-pay/process',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        });
    }
}
